/**
 * What a project is for, on its summary (AIOE 0.76.0): one line, the same one its card in Projects and Portfolio shows.
 * An owner writes it in a sheet; until then, and once they clear it, the first line of the roadmap's vision stands in,
 * and the row says where the words came from. Everyone else reads it.
 */

import { PROJECT_PURPOSE_MAX } from '@aioe/shared';
import { TargetDart } from '@gravity-ui/icons';
import { Label, TextArea, TextField } from '@heroui/react';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { Button, ErrorNote, ListGroup, ListRow } from './ui.tsx';
import { FormSheet } from './ui/FormSheet.tsx';

export function ProjectPurpose({ projectId, purpose, roadmapPurpose, owner, onChange }: { projectId: string; purpose?: string; roadmapPurpose?: string; owner: boolean; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const shown = purpose || roadmapPurpose;
  const from = purpose ? 'Written by an owner. Its card in Projects says the same.' : roadmapPurpose ? 'The first line of its roadmap’s vision, until an owner writes one.' : owner ? 'One line on what the project is for. Its card in Projects says it too.' : 'Its owners have not written one, and its roadmap has no vision yet.';
  if (!shown && !owner) return null;
  return (
    <ListGroup title="What it is for">
      <ListRow
        icon={<TargetDart />}
        title={shown ?? 'No purpose yet'}
        description={from}
        {...(owner
          ? {
              control: (
                <Button size="sm" variant="secondary" onPress={() => setEditing(true)} aria-label={shown ? 'Edit what the project is for' : 'Write what the project is for'}>
                  {shown ? 'Edit' : 'Write it'}
                </Button>
              ),
            }
          : {})}
      />
      {editing ? <PurposeSheet projectId={projectId} purpose={purpose} roadmapPurpose={roadmapPurpose} onClose={() => setEditing(false)} onSaved={onChange} /> : null}
    </ListGroup>
  );
}

function PurposeSheet({ projectId, purpose, roadmapPurpose, onClose, onSaved }: { projectId: string; purpose?: string; roadmapPurpose?: string; onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState(purpose ?? roadmapPurpose ?? '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const clean = text.replace(/\s+/g, ' ').trim();
  const save = async (value: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await routes.setProjectPurpose(projectId, value);
      onSaved();
      onClose();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet
      width="md"
      title="What the project is for"
      description="One line, at most 240 characters. It shows here and on the project’s card in Projects and Portfolio."
      onClose={onClose}
      footer={
        <>
          {purpose ? (
            <Button size="sm" variant="ghost" className="me-auto" isDisabled={busy} onPress={() => void save('')}>
              {roadmapPurpose ? 'Use the roadmap’s line' : 'Clear it'}
            </Button>
          ) : null}
          <Button size="sm" variant="tertiary" onPress={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={busy} isDisabled={busy || !clean || clean === purpose} onPress={() => void save(clean)}>
            Save
          </Button>
        </>
      }
    >
      <TextField variant="secondary" value={text} onChange={(v) => setText(v.replace(/[\r\n]+/g, ' ').slice(0, PROJECT_PURPOSE_MAX))} autoFocus>
        <Label>Purpose</Label>
        <TextArea rows={3} className="w-full resize-none" placeholder="Refunds that settle the same day, for every customer." />
      </TextField>
      <p className="m-0 text-end text-xs tabular-nums text-muted">
        {clean.length} of {PROJECT_PURPOSE_MAX}
      </p>
      {!purpose && roadmapPurpose ? <p className="m-0 text-xs text-muted">This starts from the roadmap’s line. Saving keeps it as the project’s own, even when the roadmap changes.</p> : null}
      <ErrorNote error={problem} />
    </FormSheet>
  );
}
